import { FullScreenUtils } from "webgl-framework";
import { Renderer } from "./Renderer";
import GUI from "lil-gui";
import { CameraMode } from "./CameraMode";
import { engine, cemetery } from "./KotlinLib";


function ready(fn: () => void) {
    if (document.readyState !== "loading") {
        fn();
    } else {
        document.addEventListener("DOMContentLoaded", fn);
    }
}


let renderer: Renderer;

ready(() => {
    renderer = new Renderer();
    renderer.ready = () => {
        initUI();
    };
    renderer.init("canvasGL", true);
});

interface Vec3Color {
    x: number;
    y: number;
    z: number;
}

/**
 * lil-gui binds colors as hex strings while the settings keep colors as Vec3 objects,
 * so each color picker is bound to a proxy created by this helper.
 */
function vec3ColorProxy(getColor: () => Vec3Color) {
    const channel = (value: number) => Math.max(0, Math.min(255, Math.round(value * 255)));

    return {
        get color(): string {
            const color = getColor();
            const rgb = (channel(color.x) << 16) | (channel(color.y) << 8) | channel(color.z);
            return `#${rgb.toString(16).padStart(6, "0")}`;
        },
        set color(value: string) {
            const color = getColor();
            const rgb = parseInt(value.replace("#", ""), 16);
            color.x = ((rgb >> 16) & 255) / 255;
            color.y = ((rgb >> 8) & 255) / 255;
            color.z = (rgb & 255) / 255;
        }
    };
}

/** The fog color doubles as the clear color of the scene. */
const fogColorProxy = vec3ColorProxy(() => renderer.settings.fogColor);
const ambientColorProxy = vec3ColorProxy(() => renderer.settings.ambientColor);
const lightmapColorProxy = vec3ColorProxy(() => renderer.settings.lmColor);

function initUI(): void {
    document.getElementById("message")?.classList.add("hidden");
    document.getElementById("canvasGL")?.classList.remove("transparent");

    const gui = new GUI();
    const dummyConfig = {
        android: () => window.open("https://play.google.com/store/apps/details?id=org.androidworks.livewallpaper.cartoonhalloween")
    };

    const fullScreenUtils = new FullScreenUtils();
    const fullscreen = {
        toggleFullscreen: () => {
            if (fullScreenUtils.isFullScreen()) {
                fullScreenUtils.exitFullScreen();
            } else {
                fullScreenUtils.enterFullScreen();
            }
        }
    };

    gui.add(
        renderer,
        "currentCameraMode",
        {
            "FrontEnd": CameraMode.FrontEnd,
            "FPS": CameraMode.FPS
        }
    ).name("Camera");

    gui.add(
        renderer.settings,
        "cameraMode",
        {
            "Random": cemetery.CameraMode.Random,
            "Orbiting": cemetery.CameraMode.Orbiting
        }
    )
        .name("Camera Mode")
        .onChange((value: any) => renderer.settings.cameraMode = value);

    gui.add(renderer.settings, "cameraPeriod", 0.1, 2, 0.1);
    // gui.add(renderer, "swipeCameraLeft").name("<<< Swipe left <<<");
    // gui.add(renderer, "swipeCameraRight").name(">>> Swipe right >>>");

    // gui.add(
    //     renderer.settings,
    //     "timeOfDay",
    //     {
    //         "Day": cemetery.TimeOfDay.Day,
    //         "Night": cemetery.TimeOfDay.Night,
    //         "Sunrise": cemetery.TimeOfDay.Sunrise,
    //         "Sunset": cemetery.TimeOfDay.Sunset
    //     }
    // )
    //     .name("Time of Day")
    //     .onChange((value: any) => renderer.settings.timeOfDay = value);

    gui.add(renderer.settings, "lowQuality");
    // gui.add(renderer.settings, "blurred");
    gui.add(renderer, "nextCamera").name("Next camera");
    gui.add(renderer, "randomCamera").name("Random camera");

    const folderDebug = gui.addFolder("Debug");
    folderDebug.close();
    folderDebug.add(renderer, "fps").name("FPS").listen().disable();
    folderDebug.add(renderer, "totalDrawCalls").name("Draw calls").listen().disable();
    folderDebug.add(renderer, "culledDrawCalls").name("Culled").listen().disable();
    folderDebug.add(
        renderer.settings,
        "renderMode",
        {
            "Complete": cemetery.RenderMode.Complete,
            "Complete with LM": cemetery.RenderMode.CompleteWithLm,
            "Diffuse only": cemetery.RenderMode.DiffuseOnly,
            "Model-space normals": cemetery.RenderMode.ModelNormals,
            "Lambert lighting": cemetery.RenderMode.Lambert,
            "SSAO only": cemetery.RenderMode.SsaoOnly
        }
    )
        .name("Render mode")
        .onChange((value: any) => renderer.settings.renderMode = value);

    folderDebug.add(renderer, "showAabbWireframes").name("Show AABB wireframes");

    // const testFolder = gui.addFolder("Test");
    // testFolder.close();
    // testFolder.add(renderer.settings, "testX", -30, 30, 0.0001);
    // testFolder.add(renderer.settings, "testY", -30, 30, 0.0001);
    // testFolder.add(renderer.settings, "testZ", -30, 30, 0.0001);

    const shadingFolder = gui.addFolder("Shading");
    shadingFolder.close();
    shadingFolder.add(renderer.settings.lightDirection, "x", -1, 1, 0.01).name("Light direction X");
    shadingFolder.add(renderer.settings.lightDirection, "y", -1, 1, 0.01).name("Light direction Y");
    shadingFolder.add(renderer.settings.lightDirection, "z", -1, 1, 0.01).name("Light direction Z");
    shadingFolder.add(renderer.settings, "lightExponent", 0, 4, 0.05).name("Light exponent");
    shadingFolder.addColor(ambientColorProxy, "color").name("Ambient color");
    shadingFolder.addColor(fogColorProxy, "color").name("Fog color");
    shadingFolder.add(renderer.settings, "fogStartDistance", 0, 2000, 1).name("Fog start");
    shadingFolder.add(renderer.settings, "fogDistance", 1, 2000, 1).name("Fog distance");
    // shadingFolder.add(renderer.settings, "lmScaleX", -0.2, 0.2, 0.0005).name("Lightmap scale X");
    // shadingFolder.add(renderer.settings, "lmScaleY", -0.2, 0.2, 0.0005).name("Lightmap scale Y");
    shadingFolder.addColor(lightmapColorProxy, "color").name("Lightmap glow color");
    shadingFolder.add(renderer.settings, "lmBrightness", 0, 4, 0.05).name("Lightmap brightness");
    shadingFolder.add(renderer.settings, "lmHeightStartFade", 0, 20, 0.05).name("Lightmap fade start");
    shadingFolder.add(renderer.settings, "lmHeightMax", 0, 20, 0.05).name("Lightmap fade end");

    const ssaoFolder = gui.addFolder("SSAO");
    ssaoFolder.add(renderer, "ssaoResolutionScale", 0.25, 1.0, 0.05).name("Resolution scale");
    ssaoFolder.add(
        renderer,
        "ssaoBlurSize",
        {
            "Unfiltered": "NONE",
            "Gaussian 2px": "KERNEL_2",
            "Gaussian 3px": "KERNEL_3"
        }
    ).name("Filter");
    ssaoFolder.add(renderer, "ssaoRadius", 0, 50, 0.5).name("Radius");
    ssaoFolder.add(renderer, "ssaoDepthRange", 0, 20, 0.1).name("Depth range");
    ssaoFolder.add(renderer, "ssaoBias", 0, 1, 0.01).name("Bias");
    ssaoFolder.add(renderer, "ssaoIntensity", 0, 3, 0.05).name("Intensity");
    ssaoFolder.close();

    gui.add(fullscreen, "toggleFullscreen").name("Toggle fullscreen");
    gui.add(dummyConfig, "android").name("Android app");

    gui.close();

    initDebugUI();
}

function initDebugUI(): void {
    const canvas = document.getElementById("canvasGL");
    if (!canvas) {
        return;
    }

    document.addEventListener("keypress", event => {
        if (event.key === "f") {
            renderer.currentCameraMode = renderer.currentCameraMode === CameraMode.FPS ? CameraMode.FrontEnd : CameraMode.FPS;
        }
    });
}
